import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import axios from 'axios';

// Change this to your local backend server port
const API_BASE_URL = process.env.NODE_ENV === 'production' 
? 'https://travelapp-bens.onrender.com' 
: 'http://localhost:5001';

// Initialize with null values
let app: FirebaseApp | null = null;
let db: ReturnType<typeof getFirestore> | null = null;
let auth: ReturnType<typeof getAuth> | null = null;

let initializationPromise: Promise<void> | null = null;

const STORAGE_KEY = 'firebaseConfigCache_v3';

// Function to initialize Firebase with config from server
const initializeFirebase = async (): Promise<void> => {
  if (app && db && auth) {
    return;
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  // Handle SSR/Node environments where window is undefined
  const storage = typeof window !== 'undefined' ? window.sessionStorage : undefined;

  // If Firebase app already exists (e.g., hot reload), reuse it without fetching config again
  if (!app && getApps().length > 0) {
    app = getApps()[0];
    db = getFirestore(app);
    auth = getAuth(app);
    return;
  }

  const createAppFromConfig = (config: any) => {
    console.log('Creating Firebase app with config:', {
      apiKey: config.apiKey ? '***' + config.apiKey.slice(-4) : 'missing',
      authDomain: config.authDomain,
      projectId: config.projectId,
      storageBucket: config.storageBucket,
      messagingSenderId: config.messagingSenderId,
      appId: config.appId ? '***' + config.appId.slice(-4) : 'missing'
    });
    
    // Validate Firebase configuration
    if (!config.apiKey || !config.authDomain || !config.projectId) {
      console.error('❌ Invalid Firebase configuration:', config);
      throw new Error('Invalid Firebase configuration - missing required fields');
    }
    
    app = initializeApp(config);
    db = getFirestore(app);
    auth = getAuth(app);
    console.log('Firebase initialized successfully');
    console.log('Auth object created:', !!auth);
    
    // DEBUG: Log the actual authDomain Firebase is using
    console.log("🔍 DEBUG - Firebase App Name:", app.name);
    console.log("🔍 DEBUG - Firebase Project ID:", app.options.projectId);
    console.log("🔍 DEBUG - Firebase Auth Domain (FINAL):", auth.app.options.authDomain);
    console.log("🔍 DEBUG - Config received from backend:", {
      authDomain: config.authDomain,
      projectId: config.projectId
    });
  };

  try {
    if (storage) {
      const cachedConfig = storage.getItem(STORAGE_KEY);
      if (cachedConfig) {
        try {
          const parsedConfig = JSON.parse(cachedConfig);
          createAppFromConfig(parsedConfig);
          return;
        } catch (cacheError) {
          console.warn('Failed to parse cached Firebase config, refetching.', cacheError);
          storage.removeItem(STORAGE_KEY);
        }
      }
    }

    initializationPromise = (async () => {
      // Add timeout to prevent hanging indefinitely
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      try {
        const response = await axios.get(`${API_BASE_URL}/api/firebase-config`, {
          withCredentials: true,
          signal: controller.signal,
          timeout: 10000
        });

        clearTimeout(timeoutId);
        const firebaseConfig = response.data;
        createAppFromConfig(firebaseConfig);

        if (storage) {
          try {
            storage.setItem(STORAGE_KEY, JSON.stringify(firebaseConfig));
          } catch (storageError) {
            console.warn('Unable to cache Firebase config in sessionStorage.', storageError);
          }
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        throw fetchError;
      }
    })();

    await initializationPromise;
  } catch (error) {
    app = null;
    db = null;
    auth = null;
    if (storage) {
      storage.removeItem(STORAGE_KEY);
    }
    console.error('Error initializing Firebase:', error);
    throw error;
  } finally {
    initializationPromise = null;
  }
};

// Clear cached Firebase config
const clearFirebaseCache = () => {
  if (typeof window !== 'undefined') {
    // Clear all versions of Firebase cache
    window.sessionStorage.removeItem('firebaseConfigCache');
    window.sessionStorage.removeItem('firebaseConfigCache_v2');
    window.sessionStorage.removeItem('firebaseConfigCache_v3');
    window.localStorage.removeItem('firebaseConfigCache');
    window.localStorage.removeItem('firebaseConfigCache_v2');
    window.localStorage.removeItem('firebaseConfigCache_v3');
    console.log('Firebase cache cleared (all versions)');
  }
};

// Force clear cache and reinitialize Firebase
const forceReinitializeFirebase = async () => {
  clearFirebaseCache();
  app = null;
  db = null;
  auth = null;
  initializationPromise = null;
  await initializeFirebase();
};

// Global error handler for Firebase API key errors
const handleFirebaseError = (error: any) => {
  if (error?.code === 'auth/api-key-not-valid' || error?.message?.includes('api-key-not-valid')) {
    console.log('🔄 Firebase API key error detected, clearing cache and retrying...');
    clearFirebaseCache();
    // Force reinitialize
    setTimeout(() => {
      forceReinitializeFirebase().catch(console.error);
    }, 1000);
  }
  console.error('Firebase error:', error);
};

// Initialize Firebase when this module is imported
initializeFirebase().catch(handleFirebaseError);

// Optional: Log when user changes (add this after auth is available)
if (typeof window !== 'undefined') {
  // Wait for auth to be available, then add the listener
  setTimeout(() => {
    if (auth) {
      auth.onAuthStateChanged(user => {
        console.log("🔍 DEBUG - Current User:", user?.email);
        console.log("🔍 DEBUG - Auth Domain in onAuthStateChanged:", auth?.app.options.authDomain);
      });
    }
  }, 1000);
}

export { app, db, auth, initializeFirebase, clearFirebaseCache, forceReinitializeFirebase };